import { LayoutComponent } from "../interfaces/LayoutComponent";
import { Layout } from "react-grid-layout";

export interface IPDLayoutConfig {
  gridItems: LayoutComponent[];
  layout: Layout[];
}

export type IPDEntryType = "assessment" | "doctor_rounds" | "nursing";

// IPD Flow Types - Now fetched from API (visit types with type='ipd')
export interface IPDFlowType {
  id: number;
  name: string;
}

// Get IPD layout for a specific flow type and entry type
export const getIPDLayout = (
  flowTypeId: number | string,
  entryType: IPDEntryType
): IPDLayoutConfig | null => {
  const key = `ipd_layouts_${flowTypeId}`;
  const saved = localStorage.getItem(key);
  if (saved) {
    try {
      const layouts = JSON.parse(saved);
      const layout = layouts[entryType];
      if (layout && layout.gridItems && layout.gridItems.length > 0) {
        // Ensure gridItems have proper structure with all required properties
        const validatedGridItems = layout.gridItems
          .filter((item: any) => item && item.id && item.component)
          .map((item: any) => {
            const validatedItem: any = {
              id: typeof item.id === 'string' ? parseInt(item.id) : (item.id || 0),
              component: item.component || 'AmsaInput',
              label: item.label || item.data?.label || 'Untitled',
              class: item.class || '',
              w: item.w || 3,
              h: item.h || 2,
              x: item.x || 0,
              y: item.y || 0,
              minH: item.minH || 2,
              maxH: item.maxH || 2,
              minW: item.minW || 1,
              maxW: item.maxW || 4,
              data: {
                key: item.data?.key || item.key || `field_${item.id}`,
                label: item.data?.label || item.label || 'Untitled',
                isRequired: item.data?.isRequired || false,
                value: item.data?.value || '',
                ...(item.data || {}),
              },
            };
            return validatedItem;
          });
        
        const validatedLayout = (layout.layout || [])
          .filter((item: any) => item && item.i)
          .map((item: any) => ({
            i: String(item.i || item.id || ''),
            x: item.x || 0,
            y: item.y || 0,
            w: item.w || 3,
            h: item.h || 2,
            minH: item.minH || 2,
            maxH: item.maxH || 2,
            minW: item.minW || 1,
            maxW: item.maxW || 4,
          }));
        
        const filteredLayout = validatedLayout.filter((layoutItem: any) =>
          validatedGridItems.some((gridItem: any) => gridItem.id.toString() === layoutItem.i)
        );
        
        if (validatedGridItems.length === 0) {
          return null;
        }
        
        return {
          gridItems: validatedGridItems,
          layout: filteredLayout,
        };
      }
    } catch (e) {
      console.error("Failed to load IPD layout", e);
    }
  }
  return null;
};

// Save IPD layout for a specific flow type
export const saveIPDLayout = (
  flowTypeId: number | string,
  layouts: Record<IPDEntryType, IPDLayoutConfig>
) => {
  const key = `ipd_layouts_${flowTypeId}`;
  try {
    localStorage.setItem(key, JSON.stringify(layouts));
  } catch (e) {
    console.error("Failed to save IPD layout", e);
  }
};

// Check if custom layout exists for a flow type and entry type
export const hasCustomIPDLayout = (
  flowTypeId: number | string,
  entryType: IPDEntryType
): boolean => {
  return getIPDLayout(flowTypeId, entryType) !== null;
};

// Get all layouts for a flow type (returns all entry types)
export const getAllIPDLayouts = (
  flowTypeId: number | string
): Record<IPDEntryType, IPDLayoutConfig> | null => {
  const key = `ipd_layouts_${flowTypeId}`;
  const saved = localStorage.getItem(key);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      console.error("Failed to load IPD layouts", e);
    }
  }
  return null;
};

